import { useId, useState } from 'react'
import { Card, ButtonGroup, Button, Grid } from '@design-systems/canvas-ui'
import { useEssentials } from 'hooks/utils/useEssentials'
import { TRANSLATION_KEYS } from 'locales/translation-keys'
import type { SalesBreakdownItem } from 'types/insights'
import {
    StorePerformanceGrouping,
    formatCompactCurrency,
    formatPercentChange,
    stringToTitleCase,
} from '../utils'
import './store-performance.scss'
import { NoData } from '../no-data/no-data'

interface StorePerformanceProps {
    pyramidData: SalesBreakdownItem[]
    departmentData: SalesBreakdownItem[]
}

interface SalesBreakdownListProps {
    data: SalesBreakdownItem[]
}

const SalesBreakdownList = ({ data }: SalesBreakdownListProps) => {
    if (data.length === 0) {
        return <NoData />
    }

    return (
        <Grid.Container
            direction="column"
            spacing="normal"
            className="hc8-wth-100"
        >
            {data.map((row) => {
                const change = row.district_sales_comp_gap_percent ?? 0
                const isNegative = change < 0
                const changeClass = isNegative
                    ? 'hc8-clr-error'
                    : 'hc8-clr-success'
                return (
                    <Grid.Item
                        key={row.name}
                        className="hc8-clr-base hc8-fs-050"
                    >
                        <Grid.Container
                            direction="row"
                            noWrap
                            align="center"
                            spacing="none"
                        >
                            <Grid.Item
                                size={{ xs: 7 }}
                                className="store-performance-values"
                            >
                                {stringToTitleCase(row.name) ?? '-'}
                            </Grid.Item>
                            <Grid.Item size={{ xs: 5 }}>
                                <Grid.Container
                                    direction="row"
                                    noWrap
                                    justify="end"
                                    spacing="none"
                                    align="center"
                                    className="hc8-ta-right"
                                >
                                    <Grid.Item
                                        size={{ xs: 6 }}
                                        className="store-performance-values"
                                    >
                                        <strong>
                                            {formatCompactCurrency(
                                                row.dollars ?? 0,
                                            )}
                                        </strong>
                                    </Grid.Item>
                                    <Grid.Item
                                        size={{ xs: 6 }}
                                        className={`${changeClass} store-performance-values`}
                                    >
                                        <strong>
                                            {formatPercentChange(change)}
                                        </strong>
                                    </Grid.Item>
                                </Grid.Container>
                            </Grid.Item>
                        </Grid.Container>
                    </Grid.Item>
                )
            })}
        </Grid.Container>
    )
}

export const StorePerformance = ({
    pyramidData,
    departmentData,
}: StorePerformanceProps) => {
    const { t } = useEssentials()
    const { storePerformance: keys } = TRANSLATION_KEYS.insights
    const headingId = useId()
    const [grouping, setGrouping] = useState<StorePerformanceGrouping>(
        StorePerformanceGrouping.Pyramid,
    )

    const rows =
        grouping === StorePerformanceGrouping.Pyramid
            ? pyramidData
            : departmentData

    return (
        <section aria-labelledby={headingId}>
            <h2 id={headingId} className="hc8-fs-250 hc8-mb-4x">
                {t(keys.title)}
            </h2>
            <Card className="hc8-pa-4x">
                <ButtonGroup
                    role="tablist"
                    aria-label={t(keys.title)}
                    isFullWidth
                    className="hc8-mb-4x"
                >
                    <Button
                        role="tab"
                        variant="default"
                        size="sm"
                        aria-selected={
                            grouping === StorePerformanceGrouping.Pyramid
                        }
                        className={
                            grouping === StorePerformanceGrouping.Pyramid
                                ? 'hc8-bg-subdued-active'
                                : ''
                        }
                        onClick={() =>
                            setGrouping(StorePerformanceGrouping.Pyramid)
                        }
                    >
                        {t(keys.pyramid)}
                    </Button>
                    <Button
                        role="tab"
                        variant="default"
                        size="sm"
                        aria-selected={
                            grouping === StorePerformanceGrouping.Department
                        }
                        className={
                            grouping === StorePerformanceGrouping.Department
                                ? 'hc8-bg-subdued-active'
                                : ''
                        }
                        onClick={() =>
                            setGrouping(StorePerformanceGrouping.Department)
                        }
                    >
                        {t(keys.department)}
                    </Button>
                </ButtonGroup>
                <SalesBreakdownList data={rows} />
            </Card>
        </section>
    )
}
